// Kept out of auth-api.ts so client components can read session.error without pulling the NextAuth
// server configuration into the browser bundle.
export const REFRESH_TOKEN_ERROR = 'RefreshTokenError';

// How long before expiry the jwt callback refreshes. It has to exceed the worst-case server render:
// a render that starts inside the buffer rotates once up front, but a render that starts just outside
// it carries the same bearer through every later `userToken()` read, and `authorizedFetch` does not
// retry server-side 401s — a measured 67s /api/products call is enough to outlive a 30s buffer.
// Must still stay well under Keycloak's access-token lifespan (300s by default): a buffer at or near
// the lifespan makes the refresh guard false from the moment the token is issued, turning every
// session read into a token-endpoint POST.
export const REFRESH_BUFFER_MS = 90_000;

// SessionProvider polls /api/auth/session on this interval, and each poll runs the jwt callback. An
// active tab does not depend on it — the API client reads the session on demand when its cached
// bearer runs out. Polling exists for the idle tab, whose refresh token Keycloak drops after its SSO
// idle timeout (30 min by default) unless a refresh grant resets it, so anything well inside that
// window works and a tighter interval only buys background requests.
export const SESSION_REFETCH_INTERVAL_SECONDS = 300;

// Posted by the re-authentication popup to the window that opened it.
export const REAUTH_COMPLETE_MESSAGE = 'sf-reauth-complete';

// While the popup is up, the opener also polls the session directly. The message is only the fast
// path: an identity provider page that sets Cross-Origin-Opener-Policy severs window.opener for the
// rest of the popup's life, and a severed opener swallows postMessage without any error to react to.
// Polling a dead session is cheap - the jwt callback returns a failed token without calling Keycloak.
// The window is generous because the user may genuinely be typing credentials in the popup.
export const REAUTH_POLL_INTERVAL_MS = 2_000;
export const REAUTH_POLL_TIMEOUT_MS = 5 * 60_000;

// How hard SessionExpiredDialog confirms a recovered session after the popup reports back. `update()`
// is a single unguarded session read: it bails out silently while the provider is already loading,
// and next-auth swallows a failed read and returns null. Either one would leave the dialog up on a
// session that is in fact repaired, so the recovery has to be confirmed rather than assumed.
export const VERIFY_ATTEMPTS = 5;
export const VERIFY_BACKOFF_MS = 400;

// How long a spent refresh token keeps returning the pair its grant produced. This is a
// single-flight lock, not a replay allowance: a page navigation fires several proxy invocations that
// all still carry the pre-rotation cookie, and without the lock each one would send its own grant.
// The window only has to outlive those in-flight requests, so it stays far below the access-token
// lifetime — a longer window would start handing out access tokens that have already expired.
export const ROTATION_LOCK_MS = 60_000;

// How long the cache remembers which token replaced which, separately from the lock above. The lock
// hands back the pair a grant produced and so cannot outlive that pair; this map holds no tokens the
// caller can spend, only the succession old -> new, so it costs nothing to keep far longer.
//
// It has to be long because a browser can hold a pre-rotation cookie indefinitely: a rotation riding
// on a request the browser discards — an aborted RSC prefetch, a cancelled navigation, a background
// tab — never lands its Set-Cookie. Once the succession is forgotten, that cookie's next request
// spends a token Keycloak has already revoked and the session is declared dead while a perfectly
// good one exists. Sized to Keycloak's default SSO session idle: past that the chained token is gone
// on the Keycloak side too, and remembering it longer buys nothing.
export const REFRESH_CHAIN_MS = 1_800_000;
